import { useNavigate, useSearchParams } from "react-router-dom";
import { Heading, Text } from "../../../../components/common/typography";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { Chip } from "../../../../components/common/chip";
import { SimpleTable } from "../../../../components/common/simple-table";
import type { SimpleTableColumn } from "../../../../components/common/simple-table";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { Select } from "../../../../components/forms/select";
import { useAdminPayments } from "../../hooks/useAdminPayments";
import { useAdminPaymentStats } from "../../hooks/useAdminPaymentStats";
import {
  PaymentDirection,
  PaymentState,
  type PaymentDto,
} from "../../data/types";
import { dollarFormat } from "../../../../utils/currencies";

const PAGE_SIZE = 10;

type TypeFilter = "all" | "receive" | "send";

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "receive", label: "Received" },
  { value: "send", label: "Sent" },
];

const SORT_OPTIONS = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
];

const VALID_TYPES = new Set<TypeFilter>(TYPE_OPTIONS.map((o) => o.value));

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : DATE_FMT.format(d);
}

export function AdminPaymentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const typeParam = searchParams.get("type") as TypeFilter | null;
  const type: TypeFilter = typeParam && VALID_TYPES.has(typeParam) ? typeParam : "all";
  const search = searchParams.get("search") ?? "";
  const sort = searchParams.get("sort") ?? "desc";
  const page = Number(searchParams.get("page") ?? "1") || 1;

  const directionFilter =
    type === "receive"
      ? PaymentDirection.INCOMING
      : type === "send"
        ? PaymentDirection.OUTGOING
        : undefined;

  const { data, isLoading } = useAdminPayments({
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
    direction: directionFilter,
    sortBy: "date",
    sortOrder: sort === "asc" ? "asc" : "desc",
  });

  const rows = data?.data ?? [];
  const pageCount = data?.meta.pageCount ?? 1;
  const currency = rows[0]?.currency ?? "QAR";

  const { data: stats } = useAdminPaymentStats();

  const setType = (next: TypeFilter) => {
    const p = new URLSearchParams(searchParams);
    if (next === "all") p.delete("type");
    else p.set("type", next);
    p.delete("page");
    setSearchParams(p);
  };

  const columns: SimpleTableColumn<PaymentDto>[] = [
    {
      key: "reference",
      label: "Orders",
      className: "flex-[2]",
      render: (r) => (
        <div className="flex flex-col">
          <Text variant="body" tone="dark">
            {r.reference ?? r.memo ?? `Payment #${r.id}`}
          </Text>
          {(r.originRef || r.saleOrderRef) && (
            <Text variant="bodyXs" tone="muted">
              {[r.originRef, r.saleOrderRef].filter(Boolean).join(" · ")}
            </Text>
          )}
        </div>
      ),
    },
    {
      key: "type",
      label: "Type",
      className: "flex-[0.8]",
      render: (r) =>
        r.direction === PaymentDirection.OUTGOING ? (
          <Chip variant="tertiary">Send</Chip>
        ) : (
          <Chip variant="approved">Receive</Chip>
        ),
    },
    {
      key: "partner",
      label: "Partner",
      className: "flex-[1.5]",
      render: (r) => (
        <Text variant="body" tone="body">
          {r.partnerName ?? "—"}
        </Text>
      ),
    },
    {
      key: "amount",
      label: `Amount (${currency})`,
      className: "flex-1",
      render: (r) => (
        <Text variant="bodyBold" tone="dark">
          {r.amount.toLocaleString()}
        </Text>
      ),
    },
    {
      key: "date",
      label: "Payment Date",
      className: "flex-[1.2]",
      render: (r) => (
        <Text variant="body" tone="body">
          {formatDate(r.date) ?? "—"}
        </Text>
      ),
    },
    {
      key: "journalName",
      label: "Payment Method",
      className: "flex-[1.2]",
      render: (r) => (
        <Text variant="body" tone="body">
          {r.journalName ?? "—"}
        </Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "flex-[0.8] flex justify-center",
      render: (r) =>
        r.state === PaymentState.PAID || r.state === PaymentState.POSTED ? (
          <Chip variant="approved">Paid</Chip>
        ) : (
          <Chip variant="expired">Unpaid</Chip>
        ),
    },
  ];

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
      <Heading variant="h2" tone="dark">
        Payments
      </Heading>

      <div className="grid grid-cols-4 gap-4 max-md:grid-cols-1 max-md:gap-3">
        <AccentStatCard accent="green" label="Total earned" value={dollarFormat(stats?.totalRevenue ?? 0, currency)} />
        <AccentStatCard accent="cyan" label="Released" value={dollarFormat(stats?.released ?? 0, currency)} />
        <AccentStatCard accent="amber" label="Pending release" value={dollarFormat(stats?.payoutsOutstanding ?? 0, currency)} />
        <AccentStatCard accent="green" label="Profit" value={dollarFormat(stats?.profit ?? 0, currency)} />
      </div>

      <div className="flex items-center gap-3 max-md:flex-col max-md:items-stretch">
        <SegmentedControl
          options={TYPE_OPTIONS}
          value={type}
          onChange={setType}
          aria-label="Filter payments by type"
          className="max-md:w-full max-md:justify-between"
        />

        <div className="flex-1">
          <SearchBar
            value={search}
            onChange={(e) => {
              const p = new URLSearchParams(searchParams);
              if (e.target.value.trim()) p.set("search", e.target.value);
              else p.delete("search");
              p.delete("page");
              setSearchParams(p);
            }}
            placeholder="Search payments"
          />
        </div>

        <Select
          name="payments-sort"
          value={sort}
          onChange={(e) => {
            const p = new URLSearchParams(searchParams);
            if (e.target.value === "desc") p.delete("sort");
            else p.set("sort", e.target.value);
            p.delete("page");
            setSearchParams(p);
          }}
          options={SORT_OPTIONS}
          density="default"
          fullWidth={false}
          className="min-w-[160px]"
        />
      </div>

      {isLoading ? (
        <ShimmerLoader message="Fetching payments…" lines={6} />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite px-6 py-12 text-center">
          <Text variant="body" tone="muted">No payments found.</Text>
        </div>
      ) : (
        <SimpleTable
          columns={columns}
          data={rows}
          keyExtractor={(r) => String(r.id)}
          onRowClick={(r) =>
            navigate(AppRoutes.ADMIN_PAYMENT_DETAILS.replace(":id", String(r.id)))
          }
          footer={
            pageCount > 1 ? (
              <div className="flex justify-center">
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={(next) => {
                    const p = new URLSearchParams(searchParams);
                    p.set("page", String(next));
                    setSearchParams(p, { replace: true });
                  }}
                />
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
